import * as EventTracker from '@src/analytics/EventTracker';
import {
  saveProducedClips,
} from '@src/services/AIClipProducer';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { LogManager } from '@src/utility/Logger';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback, useRef, useState } from 'react';

import { extractOrderedClipFragments } from './clipSequenceUtils';

import type { ReelOutline, ReelProducerConfig } from './types';
import type { ReelClipResults } from '@src/services/reelClipProducer/types';

const logger = LogManager.getLogger('ReelProducer:useReelSave');

type SaveReelParams = {
  outline: ReelOutline;
  clipResults: ReelClipResults;
  excludedBeats: Set<string>;
  config: ReelProducerConfig;
  onComplete: () => void;
};

type UseReelSaveReturn = {
  saveReel: (params: SaveReelParams) => Promise<void>;
  isSaving: boolean;
  error: string | null;
};

export function useReelSave(): UseReelSaveReturn {
  const rootStore = useStore();
  const videoCreator = useVideoCreatorStore();
  const flags = useFlagsCombination(rootStore.userIdentityStore.currentRole);

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savingRef = useRef(false);

  const saveReel = useCallback(
    async (params: SaveReelParams) => {
      if (savingRef.current) return;
      savingRef.current = true;

      // `config` is destructured here for future use: reelMetadata persistence
      // will consume it in a subsequent story (e.g., saving selected template,
      // speakers, goal alongside the reel).
      const { outline, clipResults, excludedBeats, config: _config, onComplete } =
        params;

      setError(null);

      const clips = extractOrderedClipFragments(
        outline,
        clipResults,
        excludedBeats,
      );

      if (clips.length === 0) {
        setError('No clips to save. Include at least one beat with a resolved clip.');
        logger.warn('saveReel: no clips to save');
        return;
      }

      setIsSaving(true);

      try {
        logger.info('saveReel: starting', {
          clipCount: clips.length,
          outlineTitle: outline.title,
          chapterCount: outline.chapters.length,
          excludedBeatCount: excludedBeats.size,
        });

        await saveProducedClips(
          videoCreator,
          clips,
          'content-studio',
          true, // asReel
          {
            enableLightweightClipSave: true,
            useBackgroundQueue: flags.enableBackgroundClipSave,
            reelTitle: outline.title,
          },
        );

        const totalDuration = clips.reduce((sum, c) => sum + c.duration, 0);

        logger.info('saveReel: complete', {
          clipCount: clips.length,
          totalDuration,
        });

        onComplete();

        EventTracker.trackReelSaved({
          story_id: videoCreator.story?.id,
          clips_count: clips.length,
          chapter_count: outline.chapters.length,
          excluded_beats_count: excludedBeats.size,
          total_duration: totalDuration,
          outline_title: outline.title,
        });
      } catch (e) {
        const message =
          e instanceof Error ? e.message : 'Failed to save reel';
        logger.error('saveReel: failed', { error: message });
        setError(message);
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [videoCreator, flags.enableBackgroundClipSave],
  );

  return { saveReel, isSaving, error };
}
